/*
 * The six states of V1_2_SPEC.md §5.1, and the one rule the whole page turns on:
 *
 *   **Nothing missing, unmeasured or unreachable is ever rendered as operational.**
 *
 * That rule is not enforced by remembering it at each call site. It is enforced
 * by `toStatusState` below, which is the only way a string off the wire becomes
 * a state, and which answers `unknown` for everything it does not recognise —
 * including a state a *newer backend* has introduced and this bundle has never
 * heard of. The alternative, defaulting to `operational` or narrowing with a
 * cast, is how a page tells a customer everything is fine because it did not
 * understand the word for what was wrong.
 */

/** Worst last, matching `apps/status/status_services.py`'s tuple exactly. */
export const STATUS_STATES = [
  'operational',
  'degraded_performance',
  'partial_outage',
  'major_outage',
  'maintenance',
  'unknown',
] as const

export type StatusState = (typeof STATUS_STATES)[number]

/**
 * Severity for "worst wins", copied from `status_services.SEVERITY`.
 *
 * The copy is deliberate and the ordering is not obvious, so both halves of the
 * backend's reasoning are repeated here rather than left to a reader to
 * rediscover: **`unknown` outranks `operational` and `maintenance` but is not
 * the worst thing on the list.** An outage we know about is worse news than a
 * component nobody is measuring, and sorting `unknown` above `major_outage`
 * would let one silent component hide a real outage behind the word "unknown".
 * `maintenance` is above `operational` because it is not fully normal service,
 * and below `degraded_performance` because it was expected and announced.
 *
 * The server sends the overall state, so this table is not used to re-derive it
 * — a second derivation is a second answer. It is used for the two things only
 * the client can know: which state to show when the payload has gone stale, and
 * how to order a row's emphasis.
 */
const SEVERITY: Record<StatusState, number> = {
  operational: 0,
  maintenance: 1,
  unknown: 2,
  degraded_performance: 3,
  partial_outage: 4,
  major_outage: 5,
}

const KNOWN = new Set<string>(STATUS_STATES)

/**
 * A state off the wire, narrowed — with everything unrecognised becoming
 * `unknown`.
 *
 * `Component.state` and `Region.state` are `string` in the generated schema and
 * not a union, because the backend declares them as `CharField`. That is the
 * honest declaration: the set can grow in a deploy that this bundle predates.
 * So the narrowing happens here, once, and it fails safe.
 */
export function toStatusState(value: unknown): StatusState {
  return typeof value === 'string' && KNOWN.has(value) ? (value as StatusState) : 'unknown'
}

/** The worst of the given states. No states at all is `unknown`, never green. */
export function worst(states: readonly StatusState[]): StatusState {
  let result: StatusState = 'unknown'
  let best = -1
  for (const state of states) {
    if (SEVERITY[state] > best) {
      best = SEVERITY[state]
      result = state
    }
  }
  return result
}

/**
 * Whether this state means "nobody is measuring".
 *
 * The page renders it with the hatch rather than a colour — `DESIGN_SYSTEM.md`
 * §3.4, "unmeasured is not a status" — so severity and knowledge read as two
 * different channels. A row of coloured dots cannot express that difference,
 * which is why this page has none.
 */
export function isUnmeasured(state: StatusState): boolean {
  return state === 'unknown'
}

/** The tone a state is drawn in. `unknown` has none, deliberately. */
export type StateTone = 'positive' | 'caution' | 'critical' | 'unmeasured'

export function toneFor(state: StatusState): StateTone {
  switch (state) {
    case 'operational':
      return 'positive'
    case 'maintenance':
    case 'degraded_performance':
      return 'caution'
    case 'partial_outage':
    case 'major_outage':
      return 'critical'
    case 'unknown':
      return 'unmeasured'
  }
}

/**
 * What a state is called on screen. English only (D6).
 *
 * Sentence case, and worded as an answer rather than a category: a reader who
 * arrived because their app stopped working is asking a question, and "Degraded
 * performance" answers it where "DEGRADED_PERFORMANCE" only classifies it.
 */
const LABELS: Record<StatusState, string> = {
  operational: 'Operational',
  degraded_performance: 'Degraded performance',
  partial_outage: 'Partial outage',
  major_outage: 'Major outage',
  maintenance: 'Under maintenance',
  unknown: 'Not reporting',
}

export function labelFor(state: StatusState): string {
  return LABELS[state]
}

/**
 * The overall headline. One sentence, no hedging, and never "operational"
 * unless every component actually reported it.
 *
 * `unknown` gets its own sentence rather than sharing "Some systems are
 * degraded", because §5.1's whole point is that not knowing and being degraded
 * are different facts. A reader who sees "we are not receiving data" knows to
 * check their own logs; a reader told "degraded" does not.
 */
export function headlineFor(state: StatusState): string {
  switch (state) {
    case 'operational':
      return 'All systems operational'
    case 'maintenance':
      return 'Maintenance in progress'
    case 'degraded_performance':
      return 'Some systems are degraded'
    case 'partial_outage':
      return 'Some systems are down'
    case 'major_outage':
      return 'Major outage'
    case 'unknown':
      return 'System status is not being reported'
  }
}
